import { useState } from 'react'
import { 
  Package, Clock, CheckCircle2, Truck, ChefHat, 
  User, Phone, DollarSign, X, MapPin
} from 'lucide-react'
import { Order } from '../types'

interface OrderCardProps {
  order: Order
  onAssignChef: () => void
  onAssignDelivery: () => void
  onStatusChange: (orderId: string, status: string) => void
  onViewDetails?: () => void
}

export const OrderCard = ({ 
  order, 
  onAssignChef, 
  onAssignDelivery, 
  onStatusChange,
  onViewDetails 
}: OrderCardProps) => {
  const [updatingStatus, setUpdatingStatus] = useState(false)

  const handleStatusUpdate = async (newStatus: string) => {
    setUpdatingStatus(true)
    try {
      await onStatusChange(order.id, newStatus)
    } finally {
      setUpdatingStatus(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-900 border-amber-300'
      case 'preparing': return 'bg-blue-50 text-blue-900 border-blue-300'
      case 'ready': return 'bg-indigo-50 text-indigo-900 border-indigo-300'
      case 'ready_for_pickup': return 'bg-purple-50 text-purple-900 border-purple-300'
      case 'picked_up': return 'bg-cyan-50 text-cyan-900 border-cyan-300'
      case 'delivering': return 'bg-orange-50 text-orange-900 border-orange-300'
      case 'delivered': return 'bg-emerald-50 text-emerald-900 border-emerald-300'
      case 'cancelled': return 'bg-red-50 text-red-900 border-red-300'
      default: return 'bg-gray-50 text-gray-900 border-gray-300'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente'
      case 'preparing': return 'Preparando'
      case 'ready': return 'Listo'
      case 'ready_for_pickup': return 'Listo para Recoger'
      case 'picked_up': return 'Recogido'
      case 'delivering': return 'En Camino'
      case 'delivered': return 'Entregado'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-3 sm:p-4 shadow-lg shadow-black/5 border border-white/60 hover:shadow-xl hover:shadow-black/10 hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2">
            <span className={`px-2 sm:px-2.5 py-1 rounded-xl text-[10px] sm:text-xs font-medium border ${getStatusColor(order.status)}`}>
              {getStatusText(order.status)}
            </span>
            <span className="text-[10px] sm:text-xs font-mono text-gray-500">
              #{order.id.substring(0, 8)}
            </span>
            {order.priority && (
              <span className="px-1.5 sm:px-2 py-0.5 bg-red-100 text-red-700 text-[10px] sm:text-xs font-medium rounded-xl border border-red-200">
                ⚡ PRIORIDAD
              </span>
            )}
          </div>
          <h3 className="font-semibold text-sm sm:text-base text-gray-900 mb-1.5 sm:mb-2 truncate">{order.customer_name}</h3>
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
            <span className="flex items-center gap-1 bg-white/60 backdrop-blur-sm px-2 py-1 rounded-xl border border-gray-200/50">
              <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
              <span className="truncate">{order.customer_phone}</span>
            </span>
            <span className="flex items-center gap-1 bg-white/60 backdrop-blur-sm px-2 py-1 rounded-xl border border-gray-200/50">
              <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
              RD${(order.total_amount + order.delivery_fee).toFixed(2)}
            </span>
            <span className="flex items-center gap-1 bg-white/60 backdrop-blur-sm px-2 py-1 rounded-xl border border-gray-200/50">
              <Package className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
              {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
            </span>
          </div>
          {order.delivery_address && (
            <div className="flex items-start gap-1.5 mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600">
              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{order.delivery_address}</span>
            </div>
          )}
        </div>
      </div>

      {/* Assigned Staff */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 text-[10px] sm:text-xs">
        {order.assigned_chef && (
          <div className="flex items-center gap-1 bg-blue-50/80 backdrop-blur-sm px-2 py-1 rounded-xl border border-blue-200/50">
            <ChefHat className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600 flex-shrink-0" />
            <span className="text-blue-900 font-medium truncate max-w-[100px] sm:max-w-[120px]">Chef: {order.assigned_chef.name}</span>
          </div>
        )}
        {order.assigned_delivery && (
          <div className="flex items-center gap-1 bg-orange-50/80 backdrop-blur-sm px-2 py-1 rounded-xl border border-orange-200/50">
            <Truck className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-orange-600 flex-shrink-0" />
            <span className="text-orange-900 font-medium truncate max-w-[100px] sm:max-w-[120px]">Delivery: {order.assigned_delivery.name}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-gray-700 to-gray-800 text-white text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl shadow-md shadow-gray-900/20 hover:shadow-lg hover:shadow-gray-900/30 hover:from-gray-800 hover:to-gray-900 transition-all duration-200 active:scale-95 flex-1 sm:flex-initial"
          >
            <Package className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Ver Detalles</span>
            <span className="sm:hidden">Detalles</span>
          </button>
        )}
        
        {order.status === 'pending' && (
          <>
            {!order.assigned_chef_id && (
              <button
                onClick={onAssignChef}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 hover:from-blue-600 hover:to-blue-700 transition-all duration-200 active:scale-95 flex-1 sm:flex-initial"
              >
                <ChefHat className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="hidden sm:inline">Asignar Chef</span>
                <span className="sm:hidden">Chef</span>
              </button>
            )}
          </>
        )}

        {order.status === 'preparing' && (
          <button
            onClick={() => handleStatusUpdate('ready')}
            disabled={updatingStatus}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl shadow-md shadow-indigo-500/20 hover:shadow-lg hover:shadow-indigo-500/30 hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 active:scale-95 flex-1 sm:flex-initial"
          >
            <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Marcar Listo</span>
            <span className="sm:hidden">Listo</span>
          </button>
        )}

        {order.status === 'ready' && (
          <>
            {!order.assigned_delivery_id && (
              <button
                onClick={onAssignDelivery}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 transition-all duration-200 active:scale-95 flex-1 sm:flex-initial"
              >
                <Truck className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="hidden sm:inline">Asignar Delivery</span>
                <span className="sm:hidden">Delivery</span>
              </button>
            )}
            {order.assigned_delivery_id && (
              <button
                onClick={() => handleStatusUpdate('delivering')}
                disabled={updatingStatus}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 active:scale-95 flex-1 sm:flex-initial"
              >
                <Truck className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span className="hidden sm:inline">Iniciar Entrega</span>
                <span className="sm:hidden">Iniciar</span>
              </button>
            )}
          </>
        )}

        {order.status === 'delivering' && (
          <button
            onClick={() => handleStatusUpdate('delivered')}
            disabled={updatingStatus}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30 hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 active:scale-95 flex-1 sm:flex-initial"
          >
            <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Marcar Entregado</span>
            <span className="sm:hidden">Entregado</span>
          </button>
        )}

        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <button
            onClick={() => handleStatusUpdate('cancelled')}
            disabled={updatingStatus}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/80 backdrop-blur-sm text-red-600 text-[10px] sm:text-xs font-medium rounded-xl sm:rounded-2xl border-2 border-red-200/50 hover:bg-red-50/80 hover:border-red-300/50 shadow-md transition-all duration-200 disabled:opacity-50 active:scale-95 flex-1 sm:flex-initial"
          >
            <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span>Cancelar</span>
          </button>
        )}
      </div>
    </div>
  )
}

